import { jsPDF } from 'jspdf';
import { vehicleTitle } from './format';

const brand = {
  dark: [18, 18, 18],
  gold: [216, 171, 18],
  muted: [98, 98, 98],
  soft: [245, 247, 250],
  line: [224, 227, 231],
};

export function passportFileName(vehicleId) {
  const short = `${vehicleId || 'vehicle'}`.slice(0, 8);
  return `vehicle-passport-${short}.pdf`;
}

async function tryLoadImageDataUrl(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function imageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  const match = dataUrl.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/);
  if (!match) return null;
  const subtype = match[1].toLowerCase();
  if (subtype.includes('png')) return 'PNG';
  if (subtype.includes('jpg') || subtype.includes('jpeg')) return 'JPEG';
  if (subtype.includes('webp')) return 'WEBP';
  return null;
}

function ensureSpace(doc, y, need, margin, pageHeight) {
  if (y + need < pageHeight - margin) return y;
  doc.addPage();
  return margin;
}

function sectionTitle(doc, margin, y, title) {
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, margin, y);
  return y + 18;
}

function bulletLines(doc, { margin, y, pageHeight, contentWidth, lines }) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...brand.muted);
  let cursor = y;
  for (const line of lines) {
    if (!line) continue;
    const wrapped = doc.splitTextToSize(`• ${line}`, contentWidth);
    cursor = ensureSpace(doc, cursor, wrapped.length * 12 + 4, margin, pageHeight);
    doc.text(wrapped, margin, cursor);
    cursor += wrapped.length * 12 + 4;
  }
  return cursor + 8;
}

function partSummary(row) {
  const cat = row.category || 'spare';
  if (cat === 'oil') {
    return [
      `Oil: ${row.oil_spec || row.name || 'Engine oil'}`,
      row.quantity != null ? `${row.quantity}${row.unit || 'L'}` : null,
      row.install_date || null,
      row.notes || null,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (cat === 'tire') {
    return [
      `Tire ${row.event_type || 'work'}`,
      row.tire_position,
      row.name,
      row.install_date,
      row.notes,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (cat === 'battery') {
    return [`Battery: ${row.name || 'Installed'}`, row.install_date, row.notes]
      .filter(Boolean)
      .join(' · ');
  }
  return [
    row.name || row.part_number || 'Part',
    row.manufacturer,
    row.warranty_months ? `${row.warranty_months} mo warranty` : null,
    row.install_date,
    row.notes,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * @param {object} passport
 * @param {object} passport.vehicle
 * @param {object} [passport.customer]
 * @param {object} [passport.business]
 * @param {Array} [passport.healthReports]
 * @param {Array} [passport.parts]
 * @param {Array} [passport.bookings]
 * @param {Array} [passport.quotations]
 */
export async function buildPassportPdf(passport = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const contentWidth = pageWidth - margin * 2;
  const vehicle = passport.vehicle || {};
  const customer = passport.customer || {};
  const business = passport.business || {};
  const healthReports = passport.healthReports || [];
  const parts = passport.parts || [];
  const bookings = passport.bookings || [];
  const quotations = passport.quotations || [];

  const logo = await tryLoadImageDataUrl(business.logo_url);
  const brandTitle = business.brand_name?.trim() || 'BW Station';
  const initials = brandTitle.slice(0, 3).toUpperCase();
  const issued = new Date().toLocaleDateString();

  // Branded dark header (same chrome as health report)
  doc.setFillColor(...brand.dark);
  doc.rect(0, 0, pageWidth, 118, 'F');

  if (logo) {
    const format = imageFormatFromDataUrl(logo);
    if (format) doc.addImage(logo, format, margin, 20, 74, 48, undefined, 'FAST');
  } else {
    doc.setFillColor(...brand.gold);
    doc.roundedRect(margin, 20, 74, 48, 8, 8, 'F');
    doc.setTextColor(...brand.dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(initials, margin + 37, 50, { align: 'center' });
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(brandTitle, margin + 90, 38);
  if (business.tagline) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(214, 214, 214);
    doc.text(business.tagline, margin + 90, 54);
  }
  const meta = [business.address, business.phone_primary, business.phone_secondary]
    .filter(Boolean)
    .join(' · ');
  if (meta) {
    doc.setFontSize(9.5);
    doc.setTextColor(220, 220, 220);
    doc.text(meta, margin + 90, 68, { maxWidth: contentWidth - 250 });
  }
  const contact = [business.support_email, business.website].filter(Boolean).join(' · ');
  if (contact) {
    doc.setFontSize(9.5);
    doc.setTextColor(220, 220, 220);
    doc.text(contact, margin + 90, 82, { maxWidth: contentWidth - 250 });
  }

  doc.setFillColor(...brand.gold);
  doc.roundedRect(pageWidth - margin - 120, 24, 120, 40, 14, 14, 'F');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PASSPORT', pageWidth - margin - 60, 48, { align: 'center' });

  let y = 140;
  doc.setFillColor(...brand.soft);
  doc.roundedRect(margin, y, contentWidth, 40, 10, 10, 'F');
  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('VEHICLE MAINTENANCE PASSPORT', margin + 14, y + 26);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...brand.muted);
  doc.text(`Issued ${issued}`, margin + contentWidth - 14, y + 26, { align: 'right' });
  y += 54;

  doc.setTextColor(...brand.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(vehicleTitle(vehicle), margin, y);
  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...brand.muted);
  const identity = [
    vehicle.plate_number ? `Plate ${vehicle.plate_number}` : null,
    vehicle.vin ? `VIN ${vehicle.vin}` : null,
    vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString()} km` : null,
    customer.full_name ||
      [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
      null,
    customer.phone || null,
  ]
    .filter(Boolean)
    .join(' · ');
  doc.text(identity || '—', margin, y, { maxWidth: contentWidth });
  y += 28;

  y = sectionTitle(doc, margin, y, 'Visits');
  if (!bookings.length) {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: ['No visits on record.'],
    });
  } else {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: bookings.slice(0, 20).map((row) => {
        const when = row.scheduled_at || row.created_at || '';
        const date = when ? new Date(when).toLocaleDateString() : '—';
        return `${date} · ${row.service_type || 'Service'} · ${row.status || ''}`;
      }),
    });
  }

  y = ensureSpace(doc, y, 40, margin, pageHeight);
  y = sectionTitle(doc, margin, y, 'Health inspections');
  if (!healthReports.length) {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: ['No finalized health inspections.'],
    });
  } else {
    for (const report of healthReports.slice(0, 12)) {
      const date = report.finalized_at
        ? new Date(report.finalized_at).toLocaleDateString()
        : '—';
      const lines = [
        `${date} · Score ${report.overall_score}/100 · ${report.bookings?.service_type || 'Inspection'}`,
        report.diagnostic_findings?.trim()
          ? `Diagnostics: ${report.diagnostic_findings.trim()}`
          : null,
        report.accident_repair_notes?.trim()
          ? `Accident / repairs: ${report.accident_repair_notes.trim()}`
          : null,
        report.maintenance_history_notes?.trim()
          ? `Notes: ${report.maintenance_history_notes.trim()}`
          : null,
      ].filter(Boolean);
      y = bulletLines(doc, { margin, y, pageHeight, contentWidth, lines });
    }
  }

  y = ensureSpace(doc, y, 40, margin, pageHeight);
  y = sectionTitle(doc, margin, y, 'Parts & fluids');
  if (!parts.length) {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: ['No oil, battery, tire, or spare-part entries yet.'],
    });
  } else {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: parts.map(partSummary),
    });
  }

  y = ensureSpace(doc, y, 40, margin, pageHeight);
  y = sectionTitle(doc, margin, y, 'Billing');
  if (!quotations.length) {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: ['No quotations or invoices on file.'],
    });
  } else {
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: quotations.slice(0, 15).map((row) => {
        const total =
          row.total != null
            ? `EGP ${Number(row.total).toFixed(2)}`
            : row.total_amount != null
              ? `EGP ${Number(row.total_amount).toFixed(2)}`
              : '';
        return `${row.status || 'quote'} · ${total} · ${
          row.sent_at || row.created_at
            ? new Date(row.sent_at || row.created_at).toLocaleDateString()
            : ''
        }`.trim();
      }),
    });
  }

  const docs = Array.isArray(vehicle.documents) ? vehicle.documents : [];
  if (docs.length) {
    y = ensureSpace(doc, y, 40, margin, pageHeight);
    y = sectionTitle(doc, margin, y, 'Documents');
    y = bulletLines(doc, {
      margin,
      y,
      pageHeight,
      contentWidth,
      lines: docs.map((docItem) => docItem.name || docItem.path || 'Document'),
    });
  }

  const footerY = pageHeight - 28;
  doc.setFontSize(9);
  doc.setTextColor(...brand.muted);
  doc.text(
    `${brandTitle} · Generated ${new Date().toLocaleString()}`,
    margin,
    footerY,
  );

  return doc;
}

export async function downloadPassportPdf(passport) {
  const doc = await buildPassportPdf(passport);
  doc.save(passportFileName(passport.vehicle?.id));
}

export async function sharePassportPdf(passport) {
  const doc = await buildPassportPdf(passport);
  const blob = doc.output('blob');
  const file = new File([blob], passportFileName(passport.vehicle?.id), {
    type: 'application/pdf',
  });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      files: [file],
      title: 'Vehicle maintenance passport',
    });
    return 'shared';
  }
  doc.save(passportFileName(passport.vehicle?.id));
  return 'downloaded';
}
